// src/features/auth/phone-verification/hooks/usePhoneVerification.ts

import {
  useEffect,
  useState,
} from 'react';

import {
  phoneVerificationService,
} from '../services/phoneVerificationService';

import type {
  PhoneVerificationPurpose,
} from '../types/phoneVerification.types';

import {
  getErrorMessage,
  normalizePhoneNumber,
  PHONE_NUMBER_PATTERN,
  VERIFICATION_CODE_PATTERN,
} from '../../signup/utils/signup.utils';


interface UsePhoneVerificationParams {
  phoneNumber: string;

  purpose:
    PhoneVerificationPurpose;
}


export const usePhoneVerification = ({
  phoneNumber,
  purpose,
}: UsePhoneVerificationParams) => {
  const [
    verificationCode,
    setVerificationCode,
  ] =
    useState('');


  const [
    verificationToken,
    setVerificationToken,
  ] =
    useState('');


  const [
    isSendingVerification,
    setIsSendingVerification,
  ] =
    useState(false);


  const [
    isVerifyingCode,
    setIsVerifyingCode,
  ] =
    useState(false);


  const [
    verificationSent,
    setVerificationSent,
  ] =
    useState(false);


  const [
    phoneVerified,
    setPhoneVerified,
  ] =
    useState(false);


  const [
    message,
    setMessage,
  ] =
    useState('');


  const [
    resendSeconds,
    setResendSeconds,
  ] =
    useState(0);


  const [
    verificationSeconds,
    setVerificationSeconds,
  ] =
    useState(0);


  /**
   * =========================================================
   * 재전송 제한 타이머
   * =========================================================
   */
  useEffect(
    () => {
      if (
        resendSeconds <= 0
      ) {
        return;
      }


      const timer =
        window.setInterval(
          () => {
            setResendSeconds(
              (previous) =>
                Math.max(
                  previous - 1,
                  0,
                ),
            );
          },
          1000,
        );


      return () => {
        window.clearInterval(
          timer,
        );
      };
    },
    [
      resendSeconds,
    ],
  );


  /**
   * =========================================================
   * 인증번호 유효시간
   * =========================================================
   */
  useEffect(
    () => {
      if (
        !verificationSent ||
        phoneVerified ||
        verificationSeconds <= 0
      ) {
        return;
      }


      const timer =
        window.setInterval(
          () => {
            setVerificationSeconds(
              (previous) =>
                Math.max(
                  previous - 1,
                  0,
                ),
            );
          },
          1000,
        );


      return () => {
        window.clearInterval(
          timer,
        );
      };
    },
    [
      verificationSent,
      phoneVerified,
      verificationSeconds,
    ],
  );


  /**
   * =========================================================
   * 인증번호 만료 처리
   * =========================================================
   */
  useEffect(
    () => {
      if (
        verificationSent &&
        !phoneVerified &&
        verificationSeconds === 0
      ) {
        setMessage(
          '인증번호가 만료되었습니다. 다시 요청해주세요.',
        );
      }
    },
    [
      verificationSent,
      phoneVerified,
      verificationSeconds,
    ],
  );


  /**
   * =========================================================
   * purpose 변경 시 기존 인증 상태 초기화
   *
   * 같은 전화번호라도
   * SIGNUP / FIND_ID / RESET_PASSWORD 용도가 다르면
   * verificationToken을 재사용하지 않도록 합니다.
   * =========================================================
   */
  useEffect(
    () => {
      setVerificationCode(
        '',
      );

      setVerificationToken(
        '',
      );

      setVerificationSent(
        false,
      );

      setPhoneVerified(
        false,
      );

      setResendSeconds(
        0,
      );

      setVerificationSeconds(
        0,
      );

      setMessage(
        '',
      );
    },
    [
      purpose,
    ],
  );


  /**
   * =========================================================
   * 인증 상태 초기화
   * =========================================================
   */
  const resetVerification =
    (): void => {
      setVerificationCode(
        '',
      );

      setVerificationToken(
        '',
      );

      setVerificationSent(
        false,
      );

      setPhoneVerified(
        false,
      );

      setResendSeconds(
        0,
      );

      setVerificationSeconds(
        0,
      );

      setMessage(
        '',
      );
    };


  /**
   * =========================================================
   * 인증번호 입력
   * =========================================================
   */
  const updateVerificationCode = (
    value: string,
  ): void => {
    setVerificationCode(
      value
        .replace(
          /\D/g,
          '',
        )
        .slice(
          0,
          6,
        ),
    );
  };


  /**
   * =========================================================
   * 인증번호 전송
   *
   * POST /api/v2/auth/phone/send
   *
   * {
   *   phoneNumber,
   *   purpose
   * }
   * =========================================================
   */
  const sendVerification =
    async (): Promise<void> => {
      const normalizedPhone =
        normalizePhoneNumber(
          phoneNumber,
        );


      if (
        !normalizedPhone
      ) {
        setMessage(
          '전화번호를 입력해주세요.',
        );

        return;
      }


      if (
        !PHONE_NUMBER_PATTERN.test(
          normalizedPhone,
        )
      ) {
        setMessage(
          '올바른 휴대전화 번호를 입력해주세요.',
        );

        return;
      }


      if (
        isSendingVerification ||
        resendSeconds > 0
      ) {
        return;
      }


      try {
        setIsSendingVerification(
          true,
        );


        setMessage(
          '',
        );


        await phoneVerificationService
          .send(
            normalizedPhone,
            purpose,
          );


        setVerificationSent(
          true,
        );


        setPhoneVerified(
          false,
        );


        setVerificationToken(
          '',
        );


        setVerificationCode(
          '',
        );


        /**
         * 재전송 제한
         *
         * 60초
         */
        setResendSeconds(
          60,
        );


        /**
         * 인증번호 유효시간
         *
         * 3분
         */
        setVerificationSeconds(
          180,
        );


        setMessage(
          '인증번호를 전송했습니다.',
        );

      } catch (
        error: unknown
      ) {
        setMessage(
          getErrorMessage(
            error,
            '인증번호 전송에 실패했습니다.',
          ),
        );

      } finally {
        setIsSendingVerification(
          false,
        );
      }
    };


  /**
   * =========================================================
   * 인증번호 확인
   *
   * POST /api/v2/auth/phone/verify
   *
   * {
   *   phoneNumber,
   *   verificationCode,
   *   purpose
   * }
   *
   * 응답:
   * {
   *   verificationToken
   * }
   * =========================================================
   */
  const verifyCode =
    async (): Promise<void> => {
      const normalizedPhone =
        normalizePhoneNumber(
          phoneNumber,
        );


      if (
        !verificationSent
      ) {
        setMessage(
          '먼저 인증번호를 요청해주세요.',
        );

        return;
      }


      if (
        verificationSeconds <= 0
      ) {
        setMessage(
          '인증번호가 만료되었습니다. 다시 요청해주세요.',
        );

        return;
      }


      if (
        !PHONE_NUMBER_PATTERN.test(
          normalizedPhone,
        )
      ) {
        setMessage(
          '올바른 휴대전화 번호를 입력해주세요.',
        );

        return;
      }


      if (
        !VERIFICATION_CODE_PATTERN.test(
          verificationCode,
        )
      ) {
        setMessage(
          '6자리 인증번호를 입력해주세요.',
        );

        return;
      }


      if (
        isVerifyingCode ||
        phoneVerified
      ) {
        return;
      }


      try {
        setIsVerifyingCode(
          true,
        );


        setMessage(
          '',
        );


        const response =
          await phoneVerificationService
            .verify(
              normalizedPhone,
              verificationCode,
              purpose,
            );


        const token =
          response
            .verificationToken;


        if (
          !token
        ) {
          throw new Error(
            '인증 토큰을 발급받지 못했습니다.',
          );
        }


        setVerificationToken(
          token,
        );


        setPhoneVerified(
          true,
        );


        setVerificationSeconds(
          0,
        );


        setMessage(
          '휴대폰 인증이 완료되었습니다.',
        );

      } catch (
        error: unknown
      ) {
        setVerificationToken(
          '',
        );


        setPhoneVerified(
          false,
        );


        setMessage(
          getErrorMessage(
            error,
            '인증번호 확인에 실패했습니다.',
          ),
        );

      } finally {
        setIsVerifyingCode(
          false,
        );
      }
    };


  return {
    verificationCode,
    verificationToken,

    verificationSent,
    phoneVerified,

    isSendingVerification,
    isVerifyingCode,

    resendSeconds,
    verificationSeconds,

    message,

    updateVerificationCode,
    sendVerification,
    verifyCode,
    resetVerification,
  };
};